import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  dataTableFootClass,
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, useTableSort } from '../../components/SortableTh';
import {
  Callout,
  ListEmptyState,
  LoadingSpinner,
  Select,
  Switch,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { Office } from '../../types/office';
import { CustomerBalancesReport } from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { dash, exportCustomerBalances, yesNo } from './exportReports';

const totalsCellClass = 'sticky bottom-0 bg-surface-muted px-3 py-2';
const totalsNumClass = `${totalsCellClass} text-right tabular-nums`;

function daysSinceLastPayment(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const startToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startPayment = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  return Math.max(
    0,
    Math.round((startToday.getTime() - startPayment.getTime()) / 86400000),
  );
}

const balanceSortGetters: Record<
  string,
  (
    row: CustomerBalancesReport['data'][number],
  ) => string | number | boolean | null | undefined
> = {
  customerName: (r) => r.customerName,
  officeName: (r) => r.officeName,
  assignedUserName: (r) => r.assignedUserName,
  chargesInvoice: (r) => r.charges.invoice,
  paymentsInvoice: (r) => r.payments.invoice,
  outstandingInvoice: (r) => r.outstanding.invoice,
  chargesCash: (r) => r.charges.cash,
  paymentsCash: (r) => r.payments.cash,
  outstandingCash: (r) => r.outstanding.cash,
  outstandingTotal: (r) => r.outstanding.total,
  lastPaymentDate: (r) => r.lastPaymentDate,
  daysSinceLastPayment: (r) => daysSinceLastPayment(r.lastPaymentDate),
  openInvoiceCount: (r) => r.openInvoiceCount,
};

export function CustomerBalancesPage() {
  const labels = t().reports;
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [assignees, setAssignees] = useState<
    Array<{ id: number; name: string; active: boolean }>
  >([]);
  const [customerId, setCustomerId] = useState('');
  const [accountingBooksCategory, setAccountingBooksCategory] = useState('');
  const [officeId, setOfficeId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [balanceFilter, setBalanceFilter] = useState<
    '' | 'positive' | 'zero' | 'negative'
  >('positive');
  const [report, setReport] = useState<CustomerBalancesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sort, toggleSort } = useTableSort('customerName');

  const displayRows = useMemo(
    () => (report ? sortItems(report.data, sort, balanceSortGetters) : []),
    [report, sort],
  );

  useEffect(() => {
    void Promise.all([
      api.getCustomerLookup(),
      api.getOffices(false),
      api.getCustomerAssignees(),
    ]).then(([customerList, officeList, assigneeList]) => {
      setCustomers(customerList);
      setOffices(officeList);
      setAssignees(assigneeList);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getCustomerBalancesReport({
          customerId: customerId ? Number(customerId) : undefined,
          activeOnly,
          balanceFilter,
          accountingBooksCategory: accountingBooksCategory || undefined,
          officeId: officeId || undefined,
          assignedUserId: assignedUserId || undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [customerId, activeOnly, balanceFilter, accountingBooksCategory, officeId, assignedUserId]);

  function handleExport(format: 'excel' | 'pdf') {
    if (!report) return;
    const balanceLabel =
      balanceFilter === 'positive'
        ? labels.balancePositive
        : balanceFilter === 'zero'
          ? labels.balanceZero
          : balanceFilter === 'negative'
            ? labels.balanceNegative
            : labels.balanceAll;
    const customerLabels = t().customers;
    const allLabel = customerLabels.filterAll;
    exportCustomerBalances({
      report,
      rows: displayRows,
      meta: [
        {
          label: t().common.customer,
          value: dash(
            customers.find((c) => String(c.id) === customerId)?.name ??
              labels.allCustomers,
          ),
        },
        { label: labels.activeOnly, value: yesNo(activeOnly) },
        { label: labels.balanceAll, value: balanceLabel },
        {
          label: customerLabels.accountingBooksCategory,
          value:
            accountingBooksCategory === 'B_SINGLE_ENTRY'
              ? customerLabels.booksSingleEntry
              : accountingBooksCategory === 'C_DOUBLE_ENTRY'
                ? customerLabels.booksDoubleEntry
                : allLabel,
        },
        {
          label: t().nav.office,
          value:
            offices.find((office) => String(office.id) === officeId)?.name ??
            allLabel,
        },
        {
          label: customerLabels.assignedUser,
          value:
            assignees.find((user) => String(user.id) === assignedUserId)
              ?.name ?? allLabel,
        },
      ],
      format,
    });
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report}
            onExcel={() => handleExport('excel')}
            onPdf={() => handleExport('pdf')}
          />
        }
      >
        <FilterField label={t().nav.customers}>
          <Select
            value={customerId}
            size="sm"
            showClearButton={Boolean(customerId)}
            placeholder={labels.allCustomers}
            options={customers.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </FilterField>
        <FilterField wide label={t().customers.accountingBooksCategory}>
          <Select
            value={accountingBooksCategory}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: t().customers.filterAll },
              {
                value: 'B_SINGLE_ENTRY',
                label: t().customers.booksSingleEntry,
              },
              {
                value: 'C_DOUBLE_ENTRY',
                label: t().customers.booksDoubleEntry,
              },
            ]}
            onChange={(e) => setAccountingBooksCategory(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().nav.office}>
          <Select
            value={officeId}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: t().customers.filterAll },
              ...offices.map((office) => ({
                value: String(office.id),
                label: office.active
                  ? office.name
                  : `${office.name} (${t().customers.inactive})`,
              })),
            ]}
            onChange={(e) => setOfficeId(e.target.value)}
          />
        </FilterField>
        <FilterField label={t().customers.assignedUser}>
          <Select
            value={assignedUserId}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: t().customers.filterAll },
              ...assignees.map((user) => ({
                value: String(user.id),
                label: user.active
                  ? user.name
                  : `${user.name} (${t().customers.inactive})`,
              })),
            ]}
            onChange={(e) => setAssignedUserId(e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.activeOnly}>
          <div className="flex h-8 items-center rounded-lg border border-line bg-surface-card px-3">
            <Switch
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              aria-label={labels.activeOnly}
              size="sm"
            />
          </div>
        </FilterField>
        <FilterField label={labels.balanceAll}>
          <Select
            value={balanceFilter}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: labels.balanceAll },
              { value: 'positive', label: labels.balancePositive },
              { value: 'zero', label: labels.balanceZero },
              { value: 'negative', label: labels.balanceNegative },
            ]}
            onChange={(e) =>
              setBalanceFilter(
                e.target.value as '' | 'positive' | 'zero' | 'negative',
              )
            }
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading || !report ? (
          <LoadingSpinner message={t().common.loading} />
        ) : report.data.length === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="w-max min-w-full border-separate border-spacing-0 text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <SortableTh
                  sortKey="customerName"
                  sort={sort}
                  onSort={toggleSort}
                  className="sticky left-0 z-30 bg-surface-muted"
                >
                  {t().common.customer}
                </SortableTh>
                <SortableTh
                  sortKey="officeName"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t().nav.office}
                </SortableTh>
                <SortableTh
                  sortKey="assignedUserName"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t().customers.assignedUser}
                </SortableTh>
                <SortableTh
                  sortKey="chargesInvoice"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.chargesInvoice}
                </SortableTh>
                <SortableTh
                  sortKey="paymentsInvoice"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.paymentsInvoiceCol}
                </SortableTh>
                <SortableTh
                  sortKey="outstandingInvoice"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.outstandingInvoice}
                </SortableTh>
                <SortableTh
                  sortKey="chargesCash"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.chargesCash}
                </SortableTh>
                <SortableTh
                  sortKey="paymentsCash"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.paymentsCashCol}
                </SortableTh>
                <SortableTh
                  sortKey="outstandingCash"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.outstandingCash}
                </SortableTh>
                <SortableTh
                  sortKey="outstandingTotal"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.outstandingTotal}
                </SortableTh>
                <SortableTh
                  sortKey="lastPaymentDate"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {t().dashboard.lastPaymentDate}
                </SortableTh>
                <SortableTh
                  sortKey="daysSinceLastPayment"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().dashboard.daysSinceLastPayment}
                </SortableTh>
                <SortableTh
                  sortKey="openInvoiceCount"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().dashboard.openInvoices}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row) => (
                <tr key={row.customerId} className="border-t border-line">
                  <td className="sticky left-0 z-10 max-w-[16rem] bg-surface-card px-3 py-2 shadow-[inset_-1px_0_0_0_rgb(var(--bf-line))]">
                    <Link
                      to={`/customers/${row.customerId}`}
                      className="block truncate text-brand hover:underline"
                      title={row.customerName}
                    >
                      {row.customerName}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {row.officeName || '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {row.assignedUserName || '—'}
                  </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.charges.invoice)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.payments.invoice)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.outstanding.invoice)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.charges.cash)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.payments.cash)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.outstanding.cash)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {formatMoney(row.outstanding.total)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {row.lastPaymentDate ? formatDate(row.lastPaymentDate) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {daysSinceLastPayment(row.lastPaymentDate) ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.openInvoiceCount}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={dataTableFootClass}>
              <tr>
                <td className={`${totalsCellClass} sticky left-0 z-30`}>
                  Σύνολα
                </td>
                <td className={totalsCellClass} />
                <td className={totalsCellClass} />
                <td className={totalsNumClass}>
                  {formatMoney(report.totals.charges.invoice)}
                </td>
                <td className={totalsNumClass}>
                  {formatMoney(report.totals.payments.invoice)}
                </td>
                <td className={totalsNumClass}>
                  {formatMoney(report.totals.outstanding.invoice)}
                </td>
                <td className={totalsNumClass}>
                  {formatMoney(report.totals.charges.cash)}
                </td>
                <td className={totalsNumClass}>
                  {formatMoney(report.totals.payments.cash)}
                </td>
                <td className={totalsNumClass}>
                  {formatMoney(report.totals.outstanding.cash)}
                </td>
                <td className={totalsNumClass}>
                  {formatMoney(report.totals.outstanding.total)}
                </td>
                <td className={totalsCellClass} />
                <td className={totalsCellClass} />
                <td className={totalsNumClass}>
                  {report.data.reduce(
                    (sum, row) => sum + row.openInvoiceCount,
                    0,
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
